import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, ArrowUp, Check, Plus, Save, Search, Trash2 } from "lucide-react";
import RoleGuard from "@/components/auth/RoleGuard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/toaster";
import { PageErrorBoundary } from "@/components/fallbacks";
import { PageLoader } from "@/components/loading/PageLoader";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import { useSubmitOrder } from "@/features/purchase-orders";
import { usePurchaseableSkus } from "@/features/inventory/hooks/usePurchaseableSkus";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { StockWriteLocationItems } from "@/features/locations/components/StockWriteLocationItems";
import { toast } from "@/hooks/use-toast";
import { reportError } from "@/lib/logging";
import { sizeRank } from "@/lib/variant-sort";
import type { Product } from "@/features/inventory/types";
import { useLocation } from "@/contexts/LocationContext";
import { LocationSelectionPrompt } from "@/components/layout/LocationSelectionPrompt";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { BackToTopPill } from "@/components/BackToTopPill";
import { getScrollContainer } from "@/lib/scroll-container";
import { cn } from "@/lib/utils";

type LineItem = {
  skuId: string;
  quantity: number;
};

const SOURCE_EMPTY_VALUE = "__none__";
const MAX_VISIBLE_RESULTS = 50;

const getProductDescription = (product: Product) => {
  const parts = [product.title, product.category.type, product.category.color, product.category.size].filter(Boolean);
  return parts.join(" · ") || product.sku;
};

const TransferCreate = () => {
  const navigate = useNavigate();

  const { selectedLocation } = useLocation();
  const { products, loading: inventoryLoading } = usePurchaseableSkus(selectedLocation?.id);
  const { createOrder, createDraftOrder, isCreating, isCreatingDraft } = useSubmitOrder();
  // BOTH lists. `locations` (delegated rows dropped) is the DESTINATION axis:
  // the destination is where the stock will be worked, so a shadow row is the
  // wrong answer there. `allLocations` is the SOURCE axis and the name lookup:
  // stock genuinely sits at delegated locations and transferring it OUT is the
  // only way to move it (BLU-879 review).
  const { locations, allLocations, isLoading: locationsLoading } = useLocations();

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sourceLocationId, setSourceLocationId] = useState("");

  // SKU search + multi-select state
  const [skuSearch, setSkuSearch] = useState("");
  const [skuDropdownOpen, setSkuDropdownOpen] = useState(false);
  const [checkedSkuIds, setCheckedSkuIds] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  usePageTitle("New Transfer", null, { label: "Back to Transfers", to: "/transfers" });

  // Actual scroll surface is SidebarInset (DESIGN.md §6) — scroll it, not window.
  useEffect(() => {
    getScrollContainer(null)?.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const productMap = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);

  const lineItemRows = useMemo(() => {
    const rows = lineItems.reduce<Array<{ lineItem: LineItem; product: Product }>>((acc, lineItem) => {
      const product = productMap.get(lineItem.skuId);
      if (!product) return acc;
      acc.push({ lineItem, product });
      return acc;
    }, []);
    rows.sort((a, b) => a.product.sku.localeCompare(b.product.sku));
    return rows;
  }, [lineItems, productMap]);

  const totalQty = useMemo(
    () => lineItemRows.reduce((sum, row) => sum + row.lineItem.quantity, 0),
    [lineItemRows],
  );

  const orderQuantities = useMemo(() => {
    return lineItems.reduce<Record<string, number>>((acc, lineItem) => {
      if (lineItem.quantity > 0) {
        acc[lineItem.skuId] = lineItem.quantity;
      }
      return acc;
    }, {});
  }, [lineItems]);

  const selectedSkuIds = useMemo(() => new Set(lineItems.map((item) => item.skuId)), [lineItems]);

  // Stepper gate: destination is the active location; a transfer needs a title
  // and a source location that differs from the destination before the line
  // items step becomes useful.
  const detailsValid = title.trim().length > 0 && sourceLocationId.length > 0;

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (title.trim().length === 0) missing.push("Title");
    if (sourceLocationId.length === 0) missing.push("Source location");
    return missing;
  }, [title, sourceLocationId]);

  // One-shot brand-purple glow on Line Items the moment Details first becomes
  // valid (DESIGN.md §4). Suppressed + short-circuited under reduced motion.
  const [playUnlockGlow, setPlayUnlockGlow] = useState(false);
  const wasValidRef = useRef(detailsValid);
  useEffect(() => {
    if (!wasValidRef.current && detailsValid) {
      const reduced =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      wasValidRef.current = detailsValid;
      if (reduced) return undefined;
      setPlayUnlockGlow(true);
      const id = window.setTimeout(() => setPlayUnlockGlow(false), 1200);
      return () => window.clearTimeout(id);
    }
    wasValidRef.current = detailsValid;
    return undefined;
  }, [detailsValid]);

  // Multi-word search: every term must match somewhere across the SKU fields.
  const { availableResults, addedResults } = useMemo(() => {
    const terms = skuSearch.trim().split(/\s+/).filter((t) => t.length > 0);
    if (terms.length === 0) return { availableResults: [] as Product[], addedResults: [] as Product[] };
    const lowerTerms = terms.map((t) => t.toLowerCase());
    const available: Product[] = [];
    const added: Product[] = [];
    for (const product of products) {
      const haystack = [
        product.sku,
        product.title || "",
        product.category.type || "",
        product.category.color || "",
        product.category.size || "",
      ].join(" ").toLowerCase();
      if (!lowerTerms.every((term) => haystack.includes(term))) continue;
      if (selectedSkuIds.has(product.id)) {
        added.push(product);
      } else {
        available.push(product);
      }
      if (available.length >= MAX_VISIBLE_RESULTS && added.length >= MAX_VISIBLE_RESULTS) break;
    }
    const sortProducts = (arr: Product[]) =>
      [...arr].sort((a, b) => {
        const colorA = (a.category.color ?? "").toLowerCase();
        const colorB = (b.category.color ?? "").toLowerCase();
        if (colorA !== colorB) return colorA.localeCompare(colorB);
        return sizeRank(a.category.size) - sizeRank(b.category.size);
      });
    return {
      availableResults: sortProducts(available.slice(0, MAX_VISIBLE_RESULTS)),
      addedResults: sortProducts(added.slice(0, MAX_VISIBLE_RESULTS)),
    };
  }, [products, skuSearch, selectedSkuIds]);

  const canSubmit =
    detailsValid &&
    Object.keys(orderQuantities).length > 0 &&
    !isCreating &&
    !isCreatingDraft;

  const upsertLineItem = (skuId: string, quantity: number, mode: "set" | "increment" = "increment") => {
    if (quantity <= 0) return;
    setLineItems((prev) => {
      const existing = prev.find((item) => item.skuId === skuId);
      if (!existing) {
        return [...prev, { skuId, quantity }];
      }
      return prev.map((item) =>
        item.skuId === skuId
          ? { ...item, quantity: mode === "set" ? quantity : item.quantity + quantity }
          : item
      );
    });
  };

  const toggleCheckedSku = useCallback((skuId: string) => {
    setCheckedSkuIds((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  }, []);

  const addCheckedSkus = useCallback(() => {
    checkedSkuIds.forEach((skuId) => {
      upsertLineItem(skuId, 1, "set");
    });
    setCheckedSkuIds(new Set());
    setSkuSearch("");
    setSkuDropdownOpen(false);
  }, [checkedSkuIds]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSkuDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && skuDropdownOpen) {
        setSkuDropdownOpen(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [skuDropdownOpen]);

  const updateLineItemQuantity = (skuId: string, value: string) => {
    if (value === "") {
      setLineItems((prev) =>
        prev.map((item) => (item.skuId === skuId ? { ...item, quantity: 0 } : item))
      );
      return;
    }
    if (!/^\d+$/.test(value)) return;
    const quantity = Number.parseInt(value, 10);
    setLineItems((prev) =>
      prev.map((item) => (item.skuId === skuId ? { ...item, quantity } : item))
    );
  };

  const removeLineItem = (skuId: string) => {
    setLineItems((prev) => prev.filter((item) => item.skuId !== skuId));
  };

  const resetForm = () => {
    setLineItems([]);
    setTitle("");
    setDescription("");
    setSourceLocationId("");
    setSkuSearch("");
    setCheckedSkuIds(new Set());
    setSkuDropdownOpen(false);
  };

  const submitTransfer = async (mode: "draft" | "submitted") => {
    if (!canSubmit) {
      toast({
        title: "Missing required fields",
        description: "Provide a title, source location, and at least one line item.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        products,
        supplierId: null,
        supplierName: "",
        orderQuantities,
        title: title.trim(),
        description: description.trim() || undefined,
        orderType: "transfer" as const,
        sourceLocationId,
      };

      const result = mode === "submitted"
        ? await createOrder(payload)
        : await createDraftOrder(payload);

      resetForm();
      const newId = (result as { data?: { id?: string } } | undefined)?.data?.id;
      navigate(newId ? `/transfers/${newId}` : "/transfers");
    } catch (error) {
      reportError("Error creating transfer", error);
    }
  };

  if (inventoryLoading || locationsLoading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="transfers" />
        </div>
      </RoleGuard>
    );
  }

  if (!locationsLoading && locations.length > 0 && !selectedLocation) {
    return (
      <LocationSelectionPrompt
        locations={locations}
        title="Select a location"
        description="Select the destination location for this transfer."
      />
    );
  }

  // A transfer needs a destination (never delegated) and a source, which MAY
  // be a delegated location, so the second location is counted off the
  // unfiltered list.
  if (!locationsLoading && (locations.length < 1 || allLocations.length < 2)) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col items-center justify-center p-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 max-w-md">
            <h2 className="text-xl font-semibold text-amber-700 mb-2">
              Need a second location
            </h2>
            <p className="text-amber-700">
              Transfers move stock between two of your locations. Add another location before creating a transfer.
            </p>
          </div>
        </div>
      </RoleGuard>
    );
  }

  if (!inventoryLoading && products.length === 0) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col items-center justify-center p-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 max-w-md">
            <h2 className="text-xl font-semibold text-amber-700 mb-2">
              No inventory available
            </h2>
            <p className="text-amber-700">
              Add inventory items before creating a transfer.
            </p>
          </div>
        </div>
      </RoleGuard>
    );
  }

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Sticky action bar — first child (DESIGN.md §3.1), always rendered
              (§3.5 create-flow exception) so the zero-state and in-progress
              state share the same chrome. */}
          <div
            className="sticky z-40 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-b bg-background/85 supports-[backdrop-filter]:bg-background/65 backdrop-blur"
            style={{ top: "var(--top-header-height, 3.5rem)" }}
          >
            <div className="mx-auto flex max-w-screen-2xl flex-wrap items-end justify-between gap-x-4 gap-y-2 pt-3 pb-2.5">
              <div className="flex flex-wrap items-end gap-2">
                <Badge variant="secondary" className="h-8 px-3 text-[11px] tabular-nums">{lineItemRows.length} Products</Badge>
                <Badge variant="secondary" className="h-8 px-3 text-[11px] tabular-nums">{totalQty.toLocaleString()} Items</Badge>
              </div>
              <div className="flex flex-wrap items-end gap-1.5">
                <Button
                  variant="outline"
                  type="button"
                  size="sm"
                  disabled={!canSubmit}
                  onClick={() => void submitTransfer("draft")}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  <Save className="h-3.5 w-3.5" />
                  {isCreatingDraft ? "Saving…" : "Save draft"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!canSubmit}
                  onClick={() => void submitTransfer("submitted")}
                  className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {isCreating ? "Submitting…" : "Submit transfer"}
                </Button>
              </div>
            </div>
          </div>

          {/* STEP 1 — Transfer details + Summary side-by-side. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Transfer details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Transfer title<span className="text-destructive ml-1">*</span></Label>
                    <Input
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder="e.g. Restock from warehouse"
                      className="max-w-prose"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Source location<span className="text-destructive ml-1">*</span></Label>
                    <Select
                      value={sourceLocationId || SOURCE_EMPTY_VALUE}
                      onValueChange={(value) => setSourceLocationId(value === SOURCE_EMPTY_VALUE ? "" : value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select source location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SOURCE_EMPTY_VALUE}>Select source location…</SelectItem>
                        <StockWriteLocationItems
                          locations={allLocations}
                          excludeId={selectedLocation?.id}
                        />
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="Optional notes for this transfer"
                    className="max-w-prose"
                  />
                </div>

                {/* Route summary: source → destination (destination = active location). */}
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {sourceLocationId
                        ? allLocations.find((l) => l.id === sourceLocationId)?.name ?? "Source"
                        : "Source"}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="font-medium text-foreground">
                      {selectedLocation?.name ?? "Destination"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Line items</span>
                  <Badge variant="secondary">{lineItemRows.length}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total qty</span>
                  <span className="tabular-nums">{totalQty.toLocaleString()}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-2 border-t inline-flex items-center gap-1">
                  <ArrowUp className="h-3 w-3" aria-hidden="true" />
                  Save draft / Submit live in the action bar above.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* STEP 2 — Line Items, gated until details are valid (DESIGN.md §4). */}
          {!detailsValid && (
            <Alert className="border-amber-300 bg-amber-50 text-amber-900">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertTitle>Fill out the required transfer details first</AlertTitle>
              <AlertDescription className="mt-1 text-amber-800">
                {missingFields.length === 1
                  ? `Missing: ${missingFields[0]}.`
                  : `Missing: ${missingFields.join(", ")}.`}
              </AlertDescription>
            </Alert>
          )}
          <div
            className={cn(
              "transition-opacity duration-300 motion-reduce:transition-none rounded-lg",
              !detailsValid && "opacity-60 select-none",
              playUnlockGlow && "motion-safe:animate-unlock-glow",
            )}
          >
            <Card
              {...(!detailsValid ? ({ inert: "" } as { inert: "" }) : {})}
              className={cn(!detailsValid && "pointer-events-none")}
            >
              <CardHeader className="space-y-3">
                <div className="flex flex-row items-center justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    Line items
                    <Badge variant="secondary">
                      {lineItemRows.length} SKU{lineItemRows.length === 1 ? "" : "s"}
                    </Badge>
                  </CardTitle>
                  {lineItemRows.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2.5 text-muted-foreground hover:text-orange-600"
                      onClick={() => setLineItems([])}
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" />
                      Clear all
                    </Button>
                  )}
                </div>

                {/* SKU search */}
                <div className="relative" ref={dropdownRef}>
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      value={skuSearch}
                      onChange={(e) => {
                        setSkuSearch(e.target.value);
                        if (e.target.value.trim()) setSkuDropdownOpen(true);
                      }}
                      onFocus={() => {
                        if (skuSearch.trim()) setSkuDropdownOpen(true);
                      }}
                      placeholder="Search SKUs by name, type, color, size..."
                      className="pl-9"
                    />
                  </div>

                  {skuDropdownOpen && skuSearch.trim() && (
                    <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-popover shadow-lg">
                      <div className="max-h-[320px] overflow-y-auto">
                        {availableResults.length === 0 && addedResults.length === 0 && (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No SKUs found.
                          </div>
                        )}

                        {availableResults.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                            onClick={() => toggleCheckedSku(product.id)}
                          >
                            <Checkbox
                              checked={checkedSkuIds.has(product.id)}
                              onCheckedChange={() => toggleCheckedSku(product.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0"
                            />
                            <span className="font-mono text-xs font-medium shrink-0">{product.sku}</span>
                            <span className="truncate text-muted-foreground">
                              {[product.title, product.category.type, product.category.color, product.category.size]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                            <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                              {product.stockLevel} in stock
                            </span>
                          </button>
                        ))}

                        {addedResults.length > 0 && (
                          <>
                            {availableResults.length > 0 && <div className="border-t" />}
                            {addedResults.map((product) => (
                              <div
                                key={product.id}
                                className="flex w-full items-center gap-3 px-3 py-2 text-sm opacity-50"
                              >
                                <Check className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <span className="font-mono text-xs font-medium shrink-0">{product.sku}</span>
                                <span className="truncate text-muted-foreground">
                                  {[product.title, product.category.type, product.category.color, product.category.size]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                                <Badge variant="secondary" className="ml-auto text-xs">
                                  Added
                                </Badge>
                              </div>
                            ))}
                          </>
                        )}
                      </div>

                      {checkedSkuIds.size > 0 && (
                        <div className="flex items-center justify-between border-t px-3 py-2">
                          <span className="text-sm text-muted-foreground">
                            {checkedSkuIds.size} selected
                          </span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setCheckedSkuIds(new Set())}
                            >
                              Clear
                            </Button>
                            <Button type="button" size="sm" onClick={addCheckedSkus}>
                              Add {checkedSkuIds.size} SKU{checkedSkuIds.size === 1 ? "" : "s"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[48px]"></TableHead>
                        <TableHead className="w-[240px]">SKU</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[120px] text-right">Qty</TableHead>
                        <TableHead className="w-[100px] text-right">In stock</TableHead>
                        <TableHead className="w-[72px] text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItemRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                            No line items yet. Search for SKUs above to add them.
                          </TableCell>
                        </TableRow>
                      ) : (
                        lineItemRows.map((row) => (
                          <TableRow key={row.product.id}>
                            <TableCell>
                              <ProductImageWithHover
                                imageUrl={row.product.image_url}
                                productSku={row.product.sku}
                                size="medium"
                              />
                            </TableCell>
                            <TableCell className="font-medium">{row.product.sku}</TableCell>
                            <TableCell>{getProductDescription(row.product)}</TableCell>
                            <TableCell className="text-right">
                              <Input
                                value={row.lineItem.quantity}
                                onChange={(event) =>
                                  updateLineItemQuantity(row.product.id, event.target.value)
                                }
                                className="ml-auto w-24 text-right"
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground tabular-nums">
                              {row.product.stockLevel}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeLineItem(row.product.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        <BackToTopPill />
        <Toaster />
      </div>
    </RoleGuard>
  );
};

export default function TransferCreateWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <TransferCreate />
    </PageErrorBoundary>
  );
}
